import { takeLatest, put, call, select, all } from "redux-saga/effects";
import { createAction } from "redux-actions";
import { api } from "../api";
import { getCommonApiParams } from "../../../../utils/redux-helpers";
import { referralModeOptions } from "../constants";

const FETCH_INITIAL_DATA = "saga/prospectsAndCustomers/FETCH_INITIAL_DATA";
export const FETCH_INITIAL_DATA_SUCCESS = "saga/prospectsAndCustomers/FETCH_INITIAL_DATA_SUCCESS";
export const FETCH_FILTERS_SUCCESS = "saga/prospectsAndCustomers/FETCH_FILTERS_SUCCESS";

export const SET_LOADING = "saga/prospectsAndCustomers/SET_LOADING";
export const SET_UPDATING = "saga/prospectsAndCustomers/SET_UPDATING";
export const SET_FILTERS_DIRTY = "saga/prospectsAndCustomers/SET_FILTERS_DIRTY";
export const SET_FILTERS_LOADING = "saga/prospectsAndCustomers/SET_FILTERS_LOADING";
export const SET_LOADING_FILTER_KEY = "saga/prospectsAndCustomers/SET_LOADING_FILTER_KEY";

export const UPDATE_FILTER = "saga/prospectsAndCustomers/UPDATE_FILTER";
export const UPDATE_FILTER_OPTIONS = "saga/prospectsAndCustomers/UPDATE_FILTER_OPTIONS";
const UPDATE_DATA = "saga/prospectsAndCustomers/UPDATE_DATA";
export const UPDATE_DATA_SUCCESS = "saga/prospectsAndCustomers/UPDATE_DATA_SUCCESS";

const RESET_FILTERS = "saga/prospectsAndCustomers/RESET_FILTERS";
export const RESET_FILTERS_SUCCESS = "saga/prospectsAndCustomers/RESET_FILTERS_SUCCESS";

const UPDATE_TYPE_OPTION = "saga/prospectsAndCustomers/UPDATE_TYPE_OPTION";
const UPDATE_CATEGORY = "saga/prospectsAndCustomers/UPDATE_CATEGORY";
const UPDATE_SALES_PACKAGE = "saga/prospectsAndCustomers/UPDATE_SALES_PACKAGE";

export const actionCreators = {
  fetchInitialData: createAction(FETCH_INITIAL_DATA),
  updateTypeOption: createAction(UPDATE_TYPE_OPTION),
  updateFilter: createAction(UPDATE_FILTER),
  updateData: createAction(UPDATE_DATA),
  resetFilters: createAction(RESET_FILTERS),
  updateCategory: createAction(UPDATE_CATEGORY),
  updateSalesPackage: createAction(UPDATE_SALES_PACKAGE)
};

export default function* watchProspectsAndCustomers() {
  yield takeLatest(FETCH_INITIAL_DATA, fetchInitialData);
  yield takeLatest(UPDATE_TYPE_OPTION, updateTypeOption);
  yield takeLatest(UPDATE_CATEGORY, updateCategoryFilter);
  yield takeLatest(UPDATE_SALES_PACKAGE, updateSalesPackageFilter);
  yield takeLatest(UPDATE_DATA, updateData);
  yield takeLatest(RESET_FILTERS, resetFilters);
}

const categoryDependencies = {
  categories: "sales_packages",
  sales_packages: "categories"
};

const typeFilterKeys = {
  membership_type: "membership_types",
  category: "categories",
  referral: "referral_categories"
};

const typeSelectedKeys = {
  membership_type: ["membership_types"],
  category: ["categories", "sales_packages"]
};

function* getFilterParams() {
  let { organization_id, short_url } = yield getCommonApiParams("commonReducer", "commonProps");
  let { selectedFilters, currentUser } = yield select((state) => state.prospectsCustomers);

  const typeId = selectedFilters.type.id;

  const params = {
    organization_id,
    short_url,
    date: currentUser.calendarEndDate,
    type: typeId,
    customer_types: selectedFilters.customerTypes.map((item) => item.id)
  };

  let filterKeys = typeSelectedKeys[typeId] || [];
  if (typeId === "referral") {
    filterKeys = [
      selectedFilters.referralMode.id === "referral_category"
        ? "referral_categories"
        : "referral_types"
    ];
  }
  for (const filterKey of filterKeys) {
    const values = selectedFilters[filterKey] || [];
    if (values.length > 0) {
      params[filterKey] = values.map((item) => item.id || item);
    }
  }

  return params;
}

function* fetchInitialData(data) {
  yield put({
    type: FETCH_INITIAL_DATA_SUCCESS,
    payload: data.payload
  });

  try {
    yield fetchFilters();
    yield fetchTables();
  } catch (error) {
    console.error(error);
  }

  yield put({ type: SET_LOADING, payload: false });
}

function* fetchFilters() {
  yield put({ type: SET_FILTERS_LOADING, payload: true });
  try {
    let { short_url } = yield getCommonApiParams("commonReducer", "commonProps");
    let { calendarEndDate } = yield select((state) => state.prospectsCustomers.currentUser);
    let type = yield select((state) => state.prospectsCustomers.selectedFilters.type);

    const { data } = yield call(api.getFilters, {
      short_url,
      date: calendarEndDate,
      type: type.id
    });

    yield put({
      type: FETCH_FILTERS_SUCCESS,
      payload: data
    });
  } catch (error) {
    console.error(error);
  }
  yield put({ type: SET_FILTERS_LOADING, payload: false });
}

function* updateTypeOption({ payload }) {
  yield put({ type: UPDATE_FILTER, payload: { key: "type", value: payload } });

  if (payload.id === "referral") {
    yield put({
      type: UPDATE_FILTER,
      payload: { key: "referralMode", value: referralModeOptions[0] }
    });
  }

  const filterOptions = yield select((state) => state.prospectsCustomers.filterOptions);
  const cacheKey = typeFilterKeys[payload.id];

  if (filterOptions[cacheKey]) {
    let filterKeys = typeSelectedKeys[payload.id] || [];
    if (payload.id === "referral") {
      filterKeys = ["referral_categories", "referral_types"];
    }
    for (const filterKey of filterKeys) {
      if (filterOptions[filterKey]) {
        yield put({
          type: UPDATE_FILTER,
          payload: { key: filterKey, value: filterOptions[filterKey] }
        });
      }
    }
  } else {
    yield fetchFilters();
  }
}

function* updateDependentFilter(sourceKey, payload) {
  const dependentKey = categoryDependencies[sourceKey];

  yield put({ type: UPDATE_FILTER, payload: { key: sourceKey, value: payload } });
  yield put({ type: SET_LOADING_FILTER_KEY, payload: dependentKey });
  try {
    let { short_url } = yield getCommonApiParams("commonReducer", "commonProps");
    let { calendarEndDate } = yield select((state) => state.prospectsCustomers.currentUser);
    let selectedValues = yield select(
      (state) => state.prospectsCustomers.selectedFilters[sourceKey]
    );

    const { data } = yield call(api.getCategories, {
      short_url,
      date: calendarEndDate,
      [sourceKey]: (selectedValues || []).map((item) => item.id).join(",")
    });

    yield put({
      type: UPDATE_FILTER,
      payload: { key: dependentKey, value: data[dependentKey] }
    });
  } catch (error) {
    console.error(error);
  }
  yield put({ type: SET_LOADING_FILTER_KEY, payload: null });
}

function* updateCategoryFilter({ payload }) {
  yield updateDependentFilter("categories", payload);
}

function* updateSalesPackageFilter({ payload }) {
  yield updateDependentFilter("sales_packages", payload);
}

function* fetchTables() {
  const params = yield getFilterParams();

  const [
    prospectsRes,
    customersRes,
    formerCustomersRes
  ] = yield all([
    call(api.getProspects, params),
    call(api.getCustomers, params),
    call(api.getFormerCustomers, params)
  ]);

  yield put({
    type: UPDATE_DATA_SUCCESS,
    payload: [
      { title: "Prospects", ...prospectsRes.data },
      { title: "Customers", ...customersRes.data },
      { title: "Former Customers", ...formerCustomersRes.data }
    ]
  });
}

function* updateData() {
  yield put({ type: SET_UPDATING, payload: true });
  yield put({ type: SET_FILTERS_DIRTY, payload: false });

  try {
    yield fetchTables();
  } catch (error) {
    console.error(error);
  }

  yield put({ type: SET_UPDATING, payload: false });
}

function* resetFilters() {
  yield put({ type: SET_UPDATING, payload: true });
  yield put({ type: RESET_FILTERS_SUCCESS });

  try {
    yield fetchTables();
  } catch (error) {
    console.error(error);
  }

  yield put({ type: SET_UPDATING, payload: false });
}
